import { create } from "zustand";

export type UploadStatus = "uploading" | "done" | "error";

export interface UploadItem {
  id: string;
  rowId?: string; // uniquely identifies exerciseId-order-setNumber
  fileName: string;
  progress: number; // 0-100
  status: UploadStatus;
  trimStart?: number;
  trimEnd?: number;
  duration?: number;
  publicUrl?: string;
  errorMessage?: string;
}

export interface UploadSuccessInfo {
  publicUrl: string;
  trimStart: number;
  trimEnd: number;
  duration: number;
}

interface UploadStore {
  items: UploadItem[];
  startUpload: (params: {
    id: string;
    rowId?: string;
    file: File;
    fileName: string;
    uploadUrl: string;
    publicUrl: string;
    trimStart: number;
    trimEnd: number;
    duration: number;
    onSuccess: (info: UploadSuccessInfo) => void;
  }) => void;
  _updateItem: (id: string, patch: Partial<UploadItem>) => void;
  _removeItem: (id: string) => void;
  hasActiveUploads: () => boolean;
}

export const useUploadStore = create<UploadStore>()((set, get) => ({
  items: [],

  hasActiveUploads: () => get().items.some((i) => i.status === "uploading"),

  _updateItem: (id, patch) =>
    set((state) => ({
      items: state.items.map((item) =>
        item.id === id ? { ...item, ...patch } : item,
      ),
    })),

  _removeItem: (id) =>
    set((state) => ({ items: state.items.filter((i) => i.id !== id) })),

  startUpload: ({
    id,
    rowId,
    file,
    fileName,
    uploadUrl,
    publicUrl,
    trimStart,
    trimEnd,
    duration,
    onSuccess,
  }) => {
    set((state) => ({
      items: [
        ...state.items,
        {
          id,
          rowId,
          fileName,
          progress: 0,
          status: "uploading",
          trimStart,
          trimEnd,
          duration,
          publicUrl,
        } satisfies UploadItem,
      ],
    }));

    const { _updateItem, _removeItem } = get();

    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener("progress", (ev) => {
      if (ev.lengthComputable) {
        _updateItem(id, {
          progress: Math.round((ev.loaded / ev.total) * 100),
        });
      }
    });

    xhr.addEventListener("load", async () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        _updateItem(id, { status: "done", progress: 100 });

        // onSuccess'e trim bilgisini de gönder — SQS tetikleme dışarıda yapılır
        onSuccess({ publicUrl, trimStart, trimEnd, duration });

        setTimeout(() => _removeItem(id), 3000);
      } else {
        _updateItem(id, {
          status: "error",
          errorMessage: `HTTP ${xhr.status}`,
        });
      }
    });

    xhr.addEventListener("error", () => {
      _updateItem(id, { status: "error", errorMessage: "Ağ hatası" });
    });

    xhr.addEventListener("abort", () => {
      _updateItem(id, { status: "error", errorMessage: "İptal edildi" });
    });

    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader("Content-Type", file.type || "video/mp4");
    xhr.send(file);
  },
}));
